import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Heart } from "lucide-react";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import TradePingDialog from "./TradePingDialog";
import ReportDialog from "./ReportDialog";

export default function CommunityCardGroup({ group, loggedIn }) {
  const [open, setOpen] = useState(false);
  const [chased, setChased] = useState(false);

  const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);
  const groupKey = `${group.name}-${group.card_number}`.replace(/\s+/g, "-").toLowerCase();

  const addToChase = async () => {
    try {
      await api.post("/chase", {
        name: group.name,
        set_name: group.set_name,
        card_number: group.card_number,
        game_type: group.game_type,
        image_url: group.image,
        source_card_id: group.owners?.[0]?.card_id,
      });
      setChased(true);
      toast.success("Added to your Chase Deck");
    } catch (e) {
      if (e.response?.status === 409) {
        setChased(true);
        toast.info("Already in your Chase Deck");
      } else if (e.response?.status === 401) {
        toast.error("Log in to build your Chase Deck");
      } else {
        toast.error("Couldn't add to Chase Deck");
      }
    }
  };

  return (
    <div data-testid={`community-card-group-${groupKey}`} className="border border-white/10 bg-[#111113] rounded-xl p-4">
      <div className="flex items-start gap-4">
        <div className="w-16 shrink-0">
          {group.image ? (
            <img
              src={pcImageUrl(resolveUrl(group.image), "240")}
              onError={pcImageFallback}
              alt={group.name}
              className="w-16 aspect-[3/4] object-cover rounded-lg border border-white/10"
            />
          ) : (
            <div className="w-16 aspect-[3/4] flex items-center justify-center text-[9px] text-zinc-700 border border-white/10 rounded-lg">No image</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{group.name}</p>
          <p className="text-xs text-zinc-500 truncate">
            {group.set_name || "Unknown set"} {group.card_number ? `· #${group.card_number}` : ""}
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            {group.total_qty} cop{group.total_qty === 1 ? "y" : "ies"} · {group.owner_count} collector{group.owner_count === 1 ? "" : "s"}
          </p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              data-testid={`community-chase-add-${groupKey}`}
              onClick={addToChase}
              disabled={chased}
              className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border transition-colors ${chased ? "border-pink-400/40 text-pink-300" : "border-white/15 text-zinc-400 hover:border-pink-400/50 hover:text-pink-300"}`}
            >
              <Heart className={`w-3 h-3 ${chased ? "fill-pink-400 text-pink-400" : ""}`} /> {chased ? "In Chase Deck" : "Chase"}
            </button>
            <button
              data-testid={`community-owners-toggle-${groupKey}`}
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-white/15 text-zinc-400 hover:text-white transition-colors"
            >
              Owners <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div data-testid={`community-owners-list-${groupKey}`} className="mt-3 pt-3 border-t border-white/10 space-y-2">
          {group.owners.map((o) => (
            <div key={o.card_id} className="flex items-center justify-between gap-2 text-sm">
              <Link to={`/collector/${o.user_id}`} className="text-[#A78BFA] hover:text-white truncate transition-colors" data-testid={`community-owner-link-${o.card_id}`}>
                {o.display_name}
              </Link>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-zinc-500">x{o.quantity}</span>
                {loggedIn && <TradePingDialog cardId={o.card_id} cardName={group.name} ownerName={o.display_name} />}
                {loggedIn && <ReportDialog cardId={o.card_id} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
